import { IDataInfoRequest, IEnquiry } from '@vivli/features/enquiry/infastructure/interface';
import { useForm } from 'react-hook-form';
import joi, { ObjectSchema } from 'joi';
import { joiResolver } from '@hookform/resolvers/joi/dist/joi';
import { JoiValidationOptionsConstant } from '@vivli/shared/infrastructure/constants';
import { EnquiryStatusEnum } from '@vivli/features/enquiry/infastructure/enum';
import { useActiveUserContext } from '@vivli/core/infrastructure/context';

const formDirSchema: ObjectSchema<IDataInfoRequest> = joi.object({
    nctId: joi
        .string()
        .regex(/^NCT\d{8}$/)
        .when('studyTitle', {
            is: joi.string().min(1),
            then: joi.string().allow('')
        })
        .label('NCT ID')
        .messages({
            'string.pattern.base': '{{#label}} must be entered in the format NCT12345678.'
        }),
    sponsorId: joi
        .string()
        .when('studyTitle', {
            is: joi.string().min(1),
            then: joi.string().allow('')
        })
        .label('Sponsor ID'),
    studyTitle: joi.string().required().label('Study Title'),
    dataType: joi.array().min(1).required().label('Data Type'),
    orgId: joi.string().required().label('Data Contributor')
});

const formEnquirySchema: ObjectSchema<IEnquiry> = joi.object({
    enquiryPurpose: joi.string().required().label('Enquiry Purpose'),
    requesterCountry: joi.string().required().label('Requester Country'),
    requesterOrganization: joi.string().required().label('Requester Organization'),
    dataInfoRequests: joi.array().items(formDirSchema).default([])
});

const defaultEnquiry: IEnquiry = {
    id: 0,
    enquiryPurpose: '',
    requesterCountry: '',
    requesterOrganization: '',
    dataInfoRequests: [],
    status: EnquiryStatusEnum.Draft
};

export function useEnquiryFormHook(enquiry: IEnquiry = defaultEnquiry, mode: 'onBlur' | 'onChange' | 'onTouched' | 'all' = 'all') {
    const { user } = useActiveUserContext();

    if (enquiry?.dataInfoRequests?.length > 0) {
        // ensure data info requests do not have null/undefined required fields
        enquiry.dataInfoRequests.forEach((dir) => {
            dir.nctId = dir.nctId ?? '';
            dir.sponsorId = dir.sponsorId ?? '';
            dir.studyTitle = dir.studyTitle ?? '';
        });
    }

    if (user && !enquiry.requesterId) {
        enquiry.requesterId = user.id;
    }

    return useForm({
        mode,
        resolver: joiResolver(formEnquirySchema.options(JoiValidationOptionsConstant)),
        defaultValues: enquiry,
        reValidateMode: 'onChange'
    });
}
